const isomorphicFetch = require('isomorphic-unfetch');
const cache = require('memory-cache');
const { toQuerystring } = require('../routing/routes');
const rollbar = require('./rollbar-agent');
const { default: isNetworkError } = require('@common.js/is-network-error');
const {
  HTTP_AUTH_USER,
  HTTP_AUTH_PASS,
  FRONTEND_CNET_URL,
  FRONTEND_GIFTS_URL,
  BACKEND_URL,
  PAYMENT_SECRET_HEADER_NAME,
  TEST_SUITE_SECRET_HEADER_NAME,
  CONSUMER_ID,
} = require('./constants');

const debug = require('debug')('cw:fetch');

function logger(error, response, url) {
  if (error) {
    if (rollbar) {
      // Convert error object into error string to pass it properly into Rollbar.
      if (typeof error.toString === 'function') {
        error = error.toString();
      }

      if (typeof window !== 'undefined') {
        // Errors on the client side.
        rollbar.error(`Client: Error during request to ${url}`, {
          userLocation: window.location.href,
          error,
        });
      } else {
        // We decided to send only 3 errors within a minute in rollbar in case when
        // backend isn't available to not rich Rollbar limit per minute .
        const storageVariableName = 'count_last_server_error_during_request_to_backend';
        const now = Date.now();
        const lastErrors = cache.get(storageVariableName);
        if (!lastErrors || (lastErrors && lastErrors.count < 3)) {
          rollbar.error(`Server: Error during request to ${url}`, { error });
          if (lastErrors) {
            cache.put(
              storageVariableName,
              { count: lastErrors.count + 1, expire: lastErrors.expire },
              +(lastErrors.expire - now),
            );
          } else {
            cache.put(storageVariableName, { count: 1, expire: now + 60 * 1000 }, 60 * 1000);
          }
        }
      }
    }

    return;
  }

  let context = {};
  // Add rollbar logging for all failed requests to the backend.
  let condition =
    response.status >= 400 &&
    response.status !== 403 && // Unpublished pages
    response.status !== 404 && // Not exiting pages
    response.status !== 414;
  if (typeof window !== 'undefined') {
    // On the client side we are interested in 404 errors as well.
    condition = response.status >= 400;
    context = {
      location: window.location.href,
      userAgent: window.navigator.userAgent,
    };
  }

  if (rollbar && condition) {
    rollbar.error(`${response.status} error during request to the ${url}`, {
      ...response,
      ...context,
    });
  }

  return response;
}

/**
 * This is universal (frontend & backend) fetch initializer.
 */
const fetch = (path, options = {}) => {
  let isExternal = false;
  let url = path;

  const internalHosts = [BACKEND_URL, FRONTEND_CNET_URL, FRONTEND_GIFTS_URL];

  // If the path doesn't start with http, append backend url.
  if (!/^https?:\/\//.test(url)) {
    url = `${BACKEND_URL}${url}`;
  } else if (internalHosts.every((host) => !path.startsWith(host))) {
    isExternal = true;
  }

  const defaultHeaders = {
    Accept: 'application/json',
  };
  // If the current environment includes http auth variables, then include them
  // as a custom header into the request.
  if (!isExternal && HTTP_AUTH_USER && HTTP_AUTH_PASS) {
    defaultHeaders['HTTP-Auth'] = `${HTTP_AUTH_USER}:${HTTP_AUTH_PASS}`;
  }
  // Set payment secret header value from browser session storage to enable test
  // payment mode.
  if (!isExternal && PAYMENT_SECRET_HEADER_NAME) {
    try {
      const secret = window.sessionStorage.getItem(PAYMENT_SECRET_HEADER_NAME);
      if (secret) {
        defaultHeaders[PAYMENT_SECRET_HEADER_NAME] = secret;
      }
    } catch {
      // Access denied - localStorage is disabled.
    }
  }

  // If this is the test suite then add this info to the backend requests.
  if (!isExternal && TEST_SUITE_SECRET_HEADER_NAME) {
    try {
      const secret = window.sessionStorage.getItem(TEST_SUITE_SECRET_HEADER_NAME);
      if (secret) {
        defaultHeaders[TEST_SUITE_SECRET_HEADER_NAME] = secret;
      }
    } catch {
      // Access denied - localStorage is disabled.
    }
  }

  const headers = {
    ...defaultHeaders,
    ...(options.headers || {}),
  };
  const defaultQuery = {};
  if (!isExternal && CONSUMER_ID) {
    defaultQuery.consumerId = CONSUMER_ID;
  }
  const query = {
    ...defaultQuery,
    ...(options.query || {}),
  };
  const requestOptions = {
    method: 'GET',
    cache: 'no-cache',
    credentials: 'same-origin',
    throwNotOk: true,
    timeout: 29000,
    ignoreStatusCodes: [],
    ...options,
    headers,
  };
  const queryString = toQuerystring(query);
  url = `${url}${queryString ? `?${queryString}` : ''}`;

  // Automatically convert data objects in JSON string.
  if (requestOptions.body && typeof requestOptions.body === 'object') {
    requestOptions.body = JSON.stringify(options.body);
  }

  // See https://stackoverflow.com/a/57888548.
  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
    debug('Fetch request aborted after %s milliseconds', requestOptions.timeout);
  }, requestOptions.timeout);
  requestOptions.signal = controller.signal;

  debug('Fetch request params:', url);

  const responsePromise = isomorphicFetch(url, requestOptions)
    .then((response) => {
      if (!requestOptions.ignoreStatusCodes.includes(response.status)) {
        logger(null, response, url);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          return {
            body: json,
            isJSON: true,
            isPlainText: false,
            statusCode: response.status,
            status: response.status,
            ok: response.ok,
          };
        });
      } else {
        return response.text().then((text) => {
          return {
            body: text,
            isJSON: false,
            isPlainText: true,
            statusCode: response.status,
            status: response.status,
            ok: response.ok,
          };
        });
      }
    })
    .then((response) => {
      if (
        requestOptions.throwNotOk &&
        !response.ok &&
        !requestOptions.ignoreStatusCodes.includes(response.status)
      ) {
        let message =
          response.body && response.body.message ? response.body.message : response.body;
        throw `${response.statusCode} ${message ? message : 'error'}`;
      }

      return response;
    })
    .catch((error) => {
      if (!isNetworkError(error)) {
        logger(error, null, url);
      }

      // Error must be thrown here anyway to leave catching
      // on the level above working.
      throw error;
    });

  return responsePromise.finally(() => clearTimeout(timeout));
};

module.exports = fetch;
